import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="pt-28 md:pt-36 pb-24">
      <Seo
        title="Tietoja meistä – JINSU"
        description="JINSU syntyi ajatuksesta, että arjen veden pitäisi olla puhdasta myös silloin, kun se koskettaa ihoasi ja hiuksiasi. Tutustu tarinaamme."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <p className="eyebrow text-water-deep/80">Tietoa meistä</p>
            <h1 className="h-display font-heading mt-5 font-semibold text-foreground" lang="fi">
              Puhdasta vettä – osaksi arjen hyvinvointia.
            </h1>
            <div className="mt-8 space-y-5 text-base md:text-lg leading-relaxed text-muted-foreground">
              <p>
                JINSU syntyi yksinkertaisesta ajatuksesta: arjen veden pitäisi olla puhdasta myös
                silloin, kun se koskettaa ihoasi ja hiuksiasi.
              </p>
              <p>
                Olen Mariam, ja olen toiminut kauneusalalla vuodesta 2009 – niin
                asiakaspalvelijana, kouluttajana kuin jälleenmyyjänä. Kauneusala on vienyt minut
                vuosien varrella ympäri maailmaa hakemaan inspiraatiota ja kouluttautumaan, ja olen
                saanut nähdä erilaisia näkökulmia ihon ja hiusten hyvinvointiin eri kulttuureissa.
              </p>
              <p>
                Näiden kokemusten kautta ymmärsin, että hyvinvointiin vaikuttavat monet tekijät –
                myös sellaiset, joita emme arjessa aina tule ajatelleeksi, kuten veden laatu.
              </p>
              <p>
                Monissa kodeissa vesi kulkee pitkän matkan putkistoissa ennen kuin se päätyy
                suihkuun tai hanaan. Matkan varrella veteen voi päätyä ruostetta, klooria ja muita
                epäpuhtauksia, jotka voivat vaikuttaa ihon ja hiusten hyvinvointiin.
              </p>
              <p>
                Tästä oivalluksesta syntyi JINSU-suihkusuodatin – yksinkertainen ja tehokas
                ratkaisu, joka puhdistaa veden juuri ennen käyttöä.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="overflow-hidden rounded-[2rem] rounded-t-[7rem] shadow-2xl shadow-primary/15">
              <img
                src="/images/mariam.webp"
                alt="Mariam, JINSU:n perustaja, suihkusuodatinpakkaus kädessään JINSU-messuosastolla"
                className="w-full aspect-[3/4] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mt-24 md:mt-36 bg-water-soft/50 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="/images/about-3.jpg"
                  alt="JINSU-tuotekuvastoa – suihkusuodatin lähikuvassa"
                  className="rounded-3xl object-cover aspect-square shadow-lg shadow-primary/10"
                  loading="lazy"
                />
                <img
                  src="/images/about-1.jpg"
                  alt="JINSU-tuotekuvastoa"
                  className="rounded-3xl object-cover aspect-square mt-8 shadow-lg shadow-primary/10"
                  loading="lazy"
                />
                <img
                  src="/images/about-4.jpg"
                  alt="JINSU-tuotekuvastoa"
                  className="rounded-3xl object-cover aspect-square shadow-lg shadow-primary/10"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
              <p className="eyebrow text-water-deep/80">Tarinamme</p>
              <h2 className="h-section font-heading mt-5 font-semibold text-foreground" lang="fi">
                JINSU sai alkunsa matkasta Souliin.
              </h2>
              <div className="mt-7 space-y-5 text-base md:text-lg leading-relaxed text-muted-foreground">
                <p>
                  Osallistuimme koulutukseen sekä Aasian suurimpiin kauneusalan messuihin, joissa
                  tutustuimme uusiin innovaatioihin ihonhoidon, veden laadun ja hyvinvoinnin
                  parissa. Messuilla nousi esiin yksi asia ylitse muiden: puhtaan veden merkitys
                  iholle ja hiuksille.
                </p>
                <p>
                  Koreassa veden laatuun ja ihonhoitorutiineihin kiinnitetään paljon huomiota, ja
                  suihkusuodattimet ovat monissa kodeissa arkipäiväinen osa hyvinvointia. Matkan
                  aikana syntyi ajatus tuoda tämä yksinkertainen mutta tehokas ratkaisu myös muiden
                  ulottuville.
                </p>
                <p>Näin syntyi JINSU.</p>
                <p>
                  JINSU yhdistää inspiroivan idean Aasian kauneusinnovaatioista, laadukkaat
                  materiaalit ja käytännöllisen muotoilun. Tavoitteemme on tarjota helppo tapa
                  parantaa arjen veden laatua – ja samalla tehdä suihkuhetkestä puhtaampi ja
                  miellyttävämpi kokemus.
                </p>
                <p className="font-heading text-foreground text-xl italic">
                  ”Jin” viittaa puhtauteen ja kirkkauteen, ”Su” tarkoittaa vettä.
                </p>
                <p>
                  Uskon, että pienillä arjen parannuksilla voi olla suuri vaikutus hyvinvointiin.
                  Puhtaampi vesi on yksi niistä.
                </p>
                <p className="font-heading text-2xl font-semibold text-water-deep">
                  JINSU – puhtaampaa vettä ihollesi ja hiuksillesi.
                </p>
              </div>
              <Link
                to="/kauppa/"
                data-testid="about-cta"
                className="mt-9 inline-flex items-center gap-2.5 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-transform duration-200 hover:scale-[1.04]"
              >
                Tutustu tuotteisiin <ArrowRight size={17} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
